/**
 * 部屋への WebSocket 接続。
 *
 * - 接続成功時に自動で `{ t: 'hello' }` を送信して現状取得
 * - 切断時は exponential backoff (200ms → 400ms → 800ms → 上限 5s) で再接続
 * - close() を明示呼び出した場合は再接続しない
 */

import type { ClientMsg, ServerMsg } from '../proto';
import type { Connection, ConnectionHandlers } from './connection';

/** 互換: 旧来 useRoom が `WsClient` を見ていた箇所への参照を温存。 */
export type WsClient = Connection;
export type WsHandlers = ConnectionHandlers;

const MAX_BACKOFF_MS = 5_000;

export function connectRoom(code: string, h: ConnectionHandlers): Connection {
  let ws: WebSocket | null = null;
  let closedByUs = false;
  let reconnecting = false;
  let attempt = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  const open = () => {
    // 再接続タイマー発火時、 その間に close() 済みなら旧部屋へ繋ぎ直さない
    // (= ゾンビ接続が共有 store を上書きするのを防ぐ)。
    if (closedByUs) return;
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const url = `${proto}://${window.location.host}/ws/room/${code}`;
    ws = new WebSocket(url);
    ws.addEventListener('open', () => {
      reconnecting = false;
      attempt = 0;
      ws?.send(JSON.stringify({ t: 'hello' } satisfies ClientMsg));
      h.onOpen?.();
    });
    ws.addEventListener('message', (ev) => {
      try {
        const msg = JSON.parse(ev.data as string) as ServerMsg;
        h.onMessage(msg);
      } catch (e) {
        console.error('WS parse error', e);
      }
    });
    ws.addEventListener('close', () => {
      h.onClose?.(closedByUs);
      if (closedByUs) return;
      reconnecting = true;
      h.onReconnect?.();
      const delay = Math.min(MAX_BACKOFF_MS, 200 * 2 ** attempt++);
      reconnectTimer = setTimeout(open, delay);
    });
    ws.addEventListener('error', () => {
      // close ハンドラが続けて呼ばれる
    });
  };
  open();

  return {
    send(msg) {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify(msg));
      }
    },
    close() {
      closedByUs = true;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      ws?.close();
    },
    isReconnecting() {
      return reconnecting;
    },
  };
}

/** クライアント側で生成する一意な move 識別子。サーバが move_rejected で返す。 */
export function newClientId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `mv_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}
